import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Download,
  LogOut,
  Lock,
  Search,
  ArrowLeft,
  Inbox,
  Trash2,
} from "lucide-react";
import {
  adminLogin,
  fetchInquiries,
  exportInquiriesUrl,
  deleteInquiries,
} from "@/lib/api";
import PressManager from "@/components/PressManager";

const STORAGE_KEY = "flysquad-admin-pwd";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [password, setPassword] = useState(
    () => sessionStorage.getItem(STORAGE_KEY) || ""
  );
  const [authed, setAuthed] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tab, setTab] = useState("inquiries");

  // Try silent auth if we already have password in session
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      (async () => {
        try {
          await fetchInquiries(stored);
          setAuthed(true);
        } catch {
          sessionStorage.removeItem(STORAGE_KEY);
        }
      })();
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    setLoading(true);
    setError("");
    fetchInquiries(password)
      .then((data) => setInquiries(data))
      .catch((e) => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Failed to load inquiries.");
      })
      .finally(() => setLoading(false));
  }, [authed, password]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return inquiries;
    return inquiries.filter((i) =>
      [
        i.name,
        i.email,
        i.phone,
        i.company,
        i.nature,
        i.heard_about,
        i.message,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [inquiries, query]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    if (!password) {
      setLoginError("Enter the admin password.");
      return;
    }
    setLoginLoading(true);
    try {
      await adminLogin(password);
      sessionStorage.setItem(STORAGE_KEY, password);
      setAuthed(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLoginError(
        typeof detail === "string" ? detail : "Invalid admin password."
      );
    } finally {
      setLoginLoading(false);
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setAuthed(false);
    setInquiries([]);
    setSelectedIds(new Set());
  };

  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((i) => selectedIds.has(i.id));

  const toggleAllFiltered = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((i) => next.delete(i.id));
      } else {
        filtered.forEach((i) => next.add(i.id));
      }
      return next;
    });

  const onConfirmDelete = async () => {
    if (selectedIds.size === 0) return;
    setDeleting(true);
    setError("");
    try {
      const ids = Array.from(selectedIds);
      await deleteInquiries(password, ids);
      setInquiries((prev) => prev.filter((i) => !selectedIds.has(i.id)));
      setSelectedIds(new Set());
      setConfirmDelete(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to delete.");
    } finally {
      setDeleting(false);
    }
  };

  const onExport = async () => {
    try {
      const res = await fetch(exportInquiriesUrl(), {
        headers: { "X-Admin-Password": password },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `flysquad-inquiries-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("Failed to export CSV.");
    }
  };

  if (!authed) {
    return (
      <div
        className="min-h-screen bg-cinematic noise-overlay flex items-center justify-center px-5"
        data-testid="admin-login-page"
      >
        <form
          onSubmit={onLogin}
          className="w-full max-w-md rounded-2xl border border-white/10 bg-fs-navy-2/80 backdrop-blur p-8"
          data-testid="admin-login-form"
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-fs-orange/15 border border-fs-orange/40">
              <Lock className="h-4 w-4 text-fs-orange" />
            </span>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-fs-orange">
                FlySquad
              </p>
              <h1 className="font-display text-2xl text-fs-cream">
                Admin Access
              </h1>
            </div>
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-fs-silver">
              Password
            </span>
            <input
              type="password"
              autoFocus
              className="fs-input mt-2"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              data-testid="admin-password-input"
            />
          </label>
          {loginError ? (
            <p
              className="mt-3 text-sm text-red-400"
              data-testid="admin-login-error"
            >
              {loginError}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={loginLoading}
            className="btn-fs-primary w-full mt-6"
            data-testid="admin-login-btn"
          >
            {loginLoading ? "Checking…" : "Sign in"}
          </button>
          <Link
            to="/"
            className="mt-4 inline-flex items-center gap-2 text-sm text-fs-silver hover:text-fs-gold transition-colors"
            data-testid="admin-back-home-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-fs-navy text-fs-cream" data-testid="admin-dashboard">
      <header className="nav-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="font-display text-xl tracking-wider text-fs-cream"
            data-testid="admin-home-link"
          >
            FlySquad <span className="text-fs-gold">·</span>{" "}
            <span className="text-fs-silver text-base">Admin</span>
          </Link>
          <div className="flex items-center gap-2">
            {tab === "inquiries" && selectedIds.size > 0 ? (
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="btn-fs-ghost !border-red-500/40 !text-red-300 hover:!border-red-400 hover:!text-red-200"
                data-testid="admin-delete-selected-btn"
              >
                <Trash2 className="h-4 w-4" />
                Delete ({selectedIds.size})
              </button>
            ) : null}
            {tab === "inquiries" ? (
              <button
                type="button"
                onClick={onExport}
                className="btn-fs-ghost"
                data-testid="admin-export-csv-btn"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
            ) : null}
            <button
              type="button"
              onClick={onLogout}
              className="btn-fs-ghost"
              data-testid="admin-logout-btn"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-1 mb-8 border-b border-white/10">
          <button
            type="button"
            onClick={() => setTab("inquiries")}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === "inquiries"
                ? "border-fs-orange text-fs-cream"
                : "border-transparent text-fs-silver hover:text-fs-cream"
            }`}
            data-testid="admin-tab-inquiries"
          >
            Inquiries
          </button>
          <button
            type="button"
            onClick={() => setTab("press")}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === "press"
                ? "border-fs-orange text-fs-cream"
                : "border-transparent text-fs-silver hover:text-fs-cream"
            }`}
            data-testid="admin-tab-press"
          >
            Press Articles
          </button>
        </div>

        {tab === "press" ? (
          <PressManager password={password} />
        ) : (
        <>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-fs-orange">
              Inbox
            </p>
            <h1 className="font-display text-3xl sm:text-4xl text-fs-cream">
              Inquiries{" "}
              <span className="text-fs-silver/60 text-2xl ml-1">
                ({inquiries.length})
              </span>
            </h1>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-fs-silver/60" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, nature…"
              className="fs-input pl-9"
              data-testid="admin-search-input"
            />
          </div>
        </div>

        {error ? (
          <div
            className="mb-4 p-3 rounded-md border border-red-500/40 bg-red-500/10 text-sm text-red-300"
            data-testid="admin-error"
          >
            {error}
          </div>
        ) : null}

        <div
          className="rounded-2xl border border-white/10 bg-fs-navy-2/60 overflow-hidden"
          data-testid="admin-table-wrap"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-[0.18em] text-fs-silver/70 bg-fs-navy/40">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      className="fs-checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleAllFiltered}
                      aria-label="Select all rows"
                      data-testid="admin-select-all-checkbox"
                    />
                  </th>
                  <Th>Date</Th>
                  <Th>Name</Th>
                  <Th>Phone</Th>
                  <Th>Email</Th>
                  <Th>Company</Th>
                  <Th>Nature</Th>
                  <Th>Heard via</Th>
                  <Th>Message</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="p-10 text-center text-fs-silver"
                      data-testid="admin-loading"
                    >
                      Loading inquiries…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="p-12 text-center text-fs-silver">
                      <div className="flex flex-col items-center gap-2">
                        <Inbox className="h-6 w-6 text-fs-silver/50" />
                        <span data-testid="admin-empty-state">
                          {inquiries.length === 0
                            ? "No inquiries yet."
                            : "No matches for your search."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((i) => (
                    <tr
                      key={i.id}
                      className={`border-t border-white/5 hover:bg-white/[0.03] cursor-pointer ${
                        selectedIds.has(i.id) ? "bg-fs-orange/[0.06]" : ""
                      }`}
                      onClick={() => setActive(i)}
                      data-testid={`admin-row-${i.id}`}
                    >
                      <td
                        className="px-4 py-3 align-top w-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="fs-checkbox"
                          checked={selectedIds.has(i.id)}
                          onChange={() => toggleOne(i.id)}
                          aria-label={`Select ${i.name}`}
                          data-testid={`admin-row-checkbox-${i.id}`}
                        />
                      </td>
                      <Td>{formatDate(i.created_at)}</Td>
                      <Td className="font-medium text-fs-cream">{i.name}</Td>
                      <Td>{i.phone}</Td>
                      <Td>
                        <a
                          href={`mailto:${i.email}`}
                          className="text-fs-gold hover:text-fs-orange"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {i.email}
                        </a>
                      </Td>
                      <Td>{i.company || "—"}</Td>
                      <Td>
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-fs-orange/15 text-fs-orange border border-fs-orange/30">
                          {i.nature}
                        </span>
                      </Td>
                      <Td>{i.heard_about || "—"}</Td>
                      <Td className="max-w-[26rem]">
                        <span className="line-clamp-2 text-fs-silver">
                          {i.message}
                        </span>
                      </Td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </main>

      {active ? (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="admin-detail-overlay"
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-white/10 bg-fs-navy-2 p-6 sm:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-fs-orange">
                  {active.nature}
                </p>
                <h3 className="font-display text-2xl sm:text-3xl text-fs-cream mt-1">
                  {active.name}
                </h3>
                <p className="text-fs-silver text-sm mt-1">
                  {formatDate(active.created_at)}
                </p>
              </div>
              <button
                className="btn-fs-ghost !py-1.5 !px-3 text-sm"
                onClick={() => setActive(null)}
                data-testid="admin-detail-close-btn"
              >
                Close
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
              <KV label="Email" value={active.email} />
              <KV label="Phone" value={active.phone} />
              <KV label="Company" value={active.company || "—"} />
              <KV label="Heard via" value={active.heard_about || "—"} />
            </div>
            <div className="mt-5">
              <p className="text-xs uppercase tracking-[0.2em] text-fs-silver/70 mb-2">
                Message
              </p>
              <p className="text-fs-cream whitespace-pre-wrap leading-relaxed">
                {active.message}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDelete ? (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !deleting && setConfirmDelete(false)}
          data-testid="admin-delete-confirm-overlay"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-red-500/30 bg-fs-navy-2 p-6 sm:p-7"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs uppercase tracking-[0.25em] text-red-400">
              Confirm delete
            </p>
            <h3 className="font-display text-2xl sm:text-3xl text-fs-cream mt-1">
              Delete {selectedIds.size}{" "}
              {selectedIds.size === 1 ? "inquiry" : "inquiries"}?
            </h3>
            <p className="text-fs-silver text-sm mt-3 leading-relaxed">
              This permanently removes the selected{" "}
              {selectedIds.size === 1 ? "row" : "rows"} from the database. This
              action cannot be undone.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
                className="btn-fs-ghost"
                data-testid="admin-delete-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirmDelete}
                disabled={deleting}
                className="btn-fs-primary !bg-none !text-white"
                style={{
                  background:
                    "linear-gradient(180deg, #ef4444 0%, #b91c1c 100%)",
                }}
                data-testid="admin-delete-confirm-btn"
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Th({ children }) {
  return <th className="px-4 py-3 font-semibold">{children}</th>;
}
function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-3 align-top text-fs-cream/90 ${className}`}>
      {children}
    </td>
  );
}
function KV({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.2em] text-fs-silver/70">
        {label}
      </p>
      <p className="text-fs-cream mt-1 break-all">{value}</p>
    </div>
  );
}
